import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomerInfo, RequiredDocumentInfo } from '../services/api';
import {
  CustomerType,
  FormStep,
  FormStepConfig,
  OnboardingFormData,
} from '../types';
import {
  useTokenValidation,
  useFormNavigation,
  useFormData,
  useFileUpload,
  useFormSubmission,
  TokenErrorType,
  UseFormDataReturn,
  UseFileUploadReturn,
  UseFormNavigationReturn,
  UseFormSubmissionReturn,
} from '../hooks';
import { validateStep } from '../utils/formValidation';

// Configuração de steps por tipo de cliente
export const getStepsForCustomerType = (customerType: CustomerType): FormStepConfig[] => {
  const commonSteps: FormStepConfig[] = [
    {
      id: 'credit-questionnaire',
      title: 'Questionário',
      description: 'Informações de crédito',
      applicableTo: ['PF', 'PJ'],
    },
    {
      id: 'authorizations',
      title: 'Autorizações',
      description: 'Declarações e termos',
      applicableTo: ['PF', 'PJ'],
    },
    {
      id: 'documents',
      title: 'Documentos',
      description: 'Upload de arquivos',
      applicableTo: ['PF', 'PJ'],
    },
    {
      id: 'review',
      title: 'Revisão',
      description: 'Conferir e enviar',
      applicableTo: ['PF', 'PJ'],
    },
  ];

  if (customerType === 'PF') {
    return [
      {
        id: 'identification',
        title: 'Identificação',
        description: 'Dados pessoais',
        applicableTo: ['PF'],
      },
      ...commonSteps,
    ];
  }

  return [
    {
      id: 'identification',
      title: 'Empresa',
      description: 'Dados da empresa',
      applicableTo: ['PJ'],
    },
    ...commonSteps,
  ];
};

// Tipo completo do contexto
export interface OnboardingContextValue {
  // Token
  token: string | null;

  // Estado de validação do token
  isLoading: boolean;
  error: string | null;
  errorType: TokenErrorType;
  customerInfo: CustomerInfo | null;
  customerType: CustomerType;
  requiredDocuments: RequiredDocumentInfo[];

  // Steps
  steps: FormStepConfig[];

  // Navegação
  navigation: UseFormNavigationReturn;

  // Dados do formulário
  formData: Partial<OnboardingFormData>;
  formHandlers: Omit<UseFormDataReturn, 'formData'>;

  // Upload de arquivos
  fileUpload: UseFileUploadReturn;

  // Submissão
  submission: UseFormSubmissionReturn;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

/**
 * Hook para acessar o contexto de onboarding.
 * Deve ser usado dentro de um OnboardingProvider.
 */
export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding deve ser usado dentro de um OnboardingProvider');
  }
  return context;
}

/**
 * Provider que agrega todos os hooks de onboarding e expõe via Context API.
 */
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // Validação de token
  const {
    isLoading,
    error: tokenError,
    errorType: tokenErrorType,
    customerInfo,
    customerType,
    requiredDocuments,
    initialFormData,
  } = useTokenValidation(token);

  const { t } = useTranslation();

  // Steps baseados no tipo de cliente
  const steps = useMemo(() => getStepsForCustomerType(customerType), [customerType]);

  // Dados do formulário (precisa ser declarado antes da navegação para o validador)
  const { formData, setFormData, ...formHandlers } = useFormData(initialFormData);

  // Upload de arquivos
  const fileUpload = useFileUpload();

  // Refs para validação (evita recriar o callback a cada render)
  const formDataRef = React.useRef(formData);
  const uploadedFilesRef = React.useRef(fileUpload.uploadedFiles);
  const requiredDocsRef = React.useRef(requiredDocuments);
  React.useEffect(() => { formDataRef.current = formData; }, [formData]);
  React.useEffect(() => { uploadedFilesRef.current = fileUpload.uploadedFiles; }, [fileUpload.uploadedFiles]);
  React.useEffect(() => { requiredDocsRef.current = requiredDocuments; }, [requiredDocuments]);

  // Validador de step (recebe o step como argumento, usa refs para dados)
  const validateCurrentStep = useCallback((step: FormStep) => {
    return validateStep(
      step,
      formDataRef.current,
      customerType,
      t,
      {
        uploadedFiles: uploadedFilesRef.current,
        requiredDocIds: requiredDocsRef.current.filter((d) => d.required).map((d) => d.id),
      },
    );
  }, [customerType, t]);

  // Navegação entre etapas (com validação)
  const navigation = useFormNavigation(steps, validateCurrentStep);

  // Submissão
  const submission = useFormSubmission(token, formData, customerType, fileUpload.uploadedFiles);

  // Combinar erros do token e da submissão
  const error = tokenError || submission.submitError;
  const errorType: TokenErrorType = tokenErrorType || submission.submitErrorType;

  const value = useMemo<OnboardingContextValue>(
    () => ({
      token,
      isLoading,
      error,
      errorType,
      customerInfo,
      customerType,
      requiredDocuments,
      steps,
      navigation,
      formData,
      formHandlers: { setFormData, ...formHandlers },
      fileUpload,
      submission,
    }),
    [
      token,
      isLoading,
      error,
      errorType,
      customerInfo,
      customerType,
      requiredDocuments,
      steps,
      navigation,
      formData,
      setFormData,
      formHandlers,
      fileUpload,
      submission,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
